import { FC } from 'react';
import { Button, Col, Row, Space, Table } from 'antd';
import {
	ArrowLeftOutlined,
	DownloadOutlined,
	FileSearchOutlined,
	ReloadOutlined,
} from '@ant-design/icons';
import FleetTitle from '@/libs/antd/components/Title';
import { useMutation, useQuery } from '@tanstack/react-query';
import {
	downloadFile,
	getDetail,
	getSessionResult,
} from '@/libs/fleet-api/services/ocr';
import { useApp } from '@/App';
import OCRDocumentStatusModal, {
	useOCRDocumentStatusModal,
} from './modals/OCRDocumentStatusModal';

const OCRPage: FC<{ customerId: string }> = ({ customerId }) => {
	const { navigate } = useApp();
	const OCRDocumentModal = useOCRDocumentStatusModal();

	const { data, isFetching, isLoading } = useQuery({
		queryKey: ['getDetail', customerId] as const,
		queryFn: ({ queryKey }) => {
			const [, params] = queryKey;
			return getDetail(params);
		},
	});
	const dataSourceOCRList = data?.data?.ocrList ?? [];
	const bankList = data?.data?.banks ?? [];

	const submitDownloadFileMutation = useMutation({
		mutationFn: downloadFile,
	});

	const refreshMutation = useMutation({
		mutationFn: getSessionResult,
	});

	return (
		<Space
			direction="vertical"
			style={{ display: 'flex' }}
			size={'large'}
		>
			<div
				style={{
					display: 'flex',
					gap: 10,
				}}
			>
				<div>
					<button
						style={{
							display: 'flex',
							height: 44,
							width: 44,
							fontSize: 24,
							border: 'none',
							alignItems: 'center',
							justifyContent: 'center',
							background: '#1557BC',
							color: 'white',
							cursor: 'pointer',
						}}
						onClick={() => {
							navigate('OCRListPage');
						}}
					>
						<ArrowLeftOutlined />
					</button>
				</div>
				<div style={{ flex: 1 }}>
					<FleetTitle>OCR Rekening</FleetTitle>
				</div>
			</div>

			<Row>
				<Col flex="auto" />
				<Col flex="none">
					<Button
						style={{
							borderRadius: 50,
							fontSize: 12,
							height: 24,
							fontWeight: 700,
							borderColor: '#1557BC',
							color: '#1557BC',
						}}
						icon={'+'}
						onClick={() => {
							navigate('OCRCreate', {
								customerId: customerId,
								bankOptions: bankList,
							});
						}}
					>
						Add Another
					</Button>
				</Col>
			</Row>

			<Table
				bordered
				loading={
					isFetching ||
					isLoading ||
					submitDownloadFileMutation.isPending ||
					refreshMutation.isPending
				}
				pagination={false}
				scroll={{ x: 'max-content' }}
				dataSource={
					dataSourceOCRList
						?.slice()
						.sort((a, b) => b.id - a.id)
						.map((item, i) => {
							return {
								...item,
								key: i,
							};
						}) ?? []
				}
				columns={[
					{
						title: 'Action',
						colSpan: 2,
						width: 100,
						align: 'center',
						render: (_, record) => (
							<a
								style={
									record.status.value !== 'Complete' ? { color: '#bababa' } : {}
								}
								onClick={(e) => {
									e.preventDefault();
									if (record.status.value !== 'Complete') return;

									navigate('OCRPageDetail', {
										ocrId: record.id,
										customerId: customerId,
										sessionId: record.sessionId,
										sessionName: record.sessionName,
									});
								}}
							>
								<FileSearchOutlined />
							</a>
						),
					},
					{
						colSpan: 0,
						width: 100,
						align: 'center',
						render: (_, record) => (
							<a
								style={
									record.status.value === 'Process' ? { color: '#bababa' } : {}
								}
								onClick={async (e) => {
									e.preventDefault();
									if (record.status.value === 'Process') return;

									const { data } = await submitDownloadFileMutation.mutateAsync(
										record.id,
									);
									if (data && data.file && data.file.url) {
										const link = document.createElement('a');
										link.href = data.file.url;
										link.download = ''; // This will trigger the download
										document.body.appendChild(link);
										link.click();
										document.body.removeChild(link);
									}
								}}
							>
								<DownloadOutlined />
							</a>
						),
					},
					{
						title: 'Session ID',
						render: (_, record) => record.sessionName,
					},
					{
						title: 'Status',
						render: (_, record) => (
							<a
								onClick={(e) => {
									e.preventDefault();

									OCRDocumentModal.open({
										ocrId: record.id,
									});
								}}
							>
								View Status
							</a>
						),
					},
					{
						title: 'Refresh Process',
						align: 'center',
						render: (_, record) => {
							return (
								<a
									style={
										record.status.value !== 'Process'
											? { color: '#bababa' }
											: {}
									}
									onClick={async (e) => {
										e.preventDefault();
										if (record.status.value !== 'Process') return;

										await refreshMutation.mutateAsync(record.id);
									}}
								>
									<ReloadOutlined />
								</a>
							);
						},
					},
				]}
			/>
			<OCRDocumentStatusModal {...OCRDocumentModal} />
		</Space>
	);
};

export default OCRPage;
